import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { fetchQuizQuestions, createQuizQuestion, updateQuizQuestion } from '@/lib/quiz'
import { QuestionForm } from '@/components/quiz/question-form'
import type { QuizQuestion } from '@/types/quiz'

export default function AdminQuizPage() {
  const [questions, setQuestions] = useState<QuizQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<QuizQuestion | null>(null)
  const [isAdding, setIsAdding] = useState(false)

  useEffect(() => {
    loadQuestions()
  }, [])

  async function loadQuestions() {
    try {
      const quizQuestions = await fetchQuizQuestions()
      setQuestions(quizQuestions || []) // Set empty array if quizQuestions is null
    } catch (error) {
      console.error('Failed to load questions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (data: Partial<QuizQuestion>) => {
    try {
      if (editing) {
        await updateQuizQuestion(editing.id, data)
      } else {
        await createQuizQuestion(data)
      }
      await loadQuestions()
      setEditing(null)
      setIsAdding(false)
    } catch (error) {
      console.error('Failed to save question:', error)
    }
  }

  if (loading) {
    return <div>Loading questions...</div>
  }

  if (editing || isAdding) {
    return (
      <div className="container mx-auto py-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-semibold mb-6">
            {editing ? 'Edit Question' : 'Add Question'}
          </h1>
          <QuestionForm
            initialData={editing || undefined}
            onSubmit={handleSubmit}
            onCancel={() => {
              setEditing(null)
              setIsAdding(false)
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold">Quiz Management</h1>
        <Button onClick={() => setIsAdding(true)}>Add Question</Button>
      </div>

      <div className="grid gap-6">
        {questions.map((question) => (
          <Card key={question.id}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>{question.question}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(question)}
                >
                  Edit
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Category: {question.category}
                </p>
                <div className="grid gap-2">
                  {question.options.map((option) => (
                    <div
                      key={option.value}
                      className="p-3 rounded-md border bg-muted/50"
                    >
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">
                        Traits: {option.traits.join(', ')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}